/**
 * Sticky Add-to-Cart Component
 *
 * Displays a sticky add-to-cart button on mobile when the main ATC button
 * is out of view. Syncs with the product form for price and variant updates.
 *
 * @extends Component
 */

import { Component } from '@theme/component';
import { isMobileBreakpoint, fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyButton'];

  constructor() {
    super();
    this.formId = this.dataset.formId;
    this.hideThreshold = parseInt(this.dataset.hideThreshold) || 400;
    this.productForm = null;
    this.mainAtcButton = null;
    this.observer = null;
    this.isVisible = false;
    this.currentVariant = null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Only run on mobile
    if (!isMobileBreakpoint()) {
      return;
    }

    this.productForm = document.getElementById(this.formId);
    if (!this.productForm) {
      return;
    }

    // Find the main add-to-cart button
    this.mainAtcButton = this.productForm.querySelector('[name="add"]');
    if (!this.mainAtcButton) {
      return;
    }

    this.setupIntersectionObserver();
    this.setupVariantListener();
    this.initializeButtonState();
    this.style.display = 'block';
  }

  /**
   * Initialize button state based on current variant availability
   */
  initializeButtonState() {
    // Get the initial variant info from the product form
    const variantIdInput = this.productForm.querySelector('[name="id"]');
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    // Try to get variant data from window or form
    let variant = null;

    // Check if variant data is available in the page
    if (window.productVariants && Array.isArray(window.productVariants)) {
      variant = window.productVariants.find(v => v.id == variantId);
    }

    // If we found variant data, update the buttons
    if (variant) {
      this.updateVariantInfo({ variant });
    } else {
      // Fallback: check if main ATC button is disabled
      const isAvailable = this.mainAtcButton && !this.mainAtcButton.disabled;
      this.toggleButtons(isAvailable, variantId);
    }
  }

  /**
   * Toggle between ATC and BIS buttons
   * @param {boolean} isAvailable - Whether the variant is available
   * @param {string} variantId - The variant ID
   */
  toggleButtons(isAvailable, variantId) {
    const atcButton = this.querySelector('.sticky-atc__button--add-to-cart');
    const bisButton = this.querySelector('.sticky-atc__button--back-in-stock');

    if (isAvailable) {
      // Show ATC button, hide BIS button
      if (atcButton) {
        atcButton.style.display = '';
        atcButton.disabled = false;
      }
      if (bisButton) {
        bisButton.style.display = 'none';
      }
    } else {
      // Hide ATC button, show BIS button
      if (atcButton) {
        atcButton.style.display = 'none';
      }
      if (bisButton) {
        bisButton.style.display = '';
        // Update BIS button's data attributes with current variant
        if (variantId) {
          bisButton.setAttribute('data-variant-id', variantId);
        }
      }
    }
  }

  /**
   * Set up Intersection Observer to detect when main ATC button is visible
   */
  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: `-${this.hideThreshold}px 0px 0px 0px`,
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Show sticky button when main button is NOT visible
        this.toggleVisibility(!entry.isIntersecting);
      });
    }, options);

    this.observer.observe(this.mainAtcButton);
  }

  /**
   * Listen for variant changes to update price and availability
   */
  setupVariantListener() {
    // Listen for variant updates on the section level (where events bubble up)
    const target = this.productForm.closest('.shopify-section');
    if (!target) return;

    target.addEventListener(ThemeEvents.variantUpdate, (event) => {
      // Check if this event is for the correct product
      if (event.detail.data.productId !== this.productForm.dataset.productId) {
        return;
      }

      const variant = event.detail.resource;
      if (variant) {
        this.updateVariantInfo({ variant });
      }
    });
  }

  /**
   * Update price and button state when variant changes
   * @param {Object} detail - Variant change event detail
   */
  updateVariantInfo(detail) {
    const { variant } = detail;

    if (!variant) return;

    this.currentVariant = variant;

    // Update price display
    if (this.refs.priceDisplay) {
      this.refs.priceDisplay.textContent = this.formatMoney(variant.price);
    }

    // Update compare-at price
    if (this.refs.comparePrice) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.refs.comparePrice.innerHTML = `<s>${this.formatMoney(variant.compare_at_price)}</s>`;
        this.refs.comparePrice.style.display = 'inline';
      } else {
        this.refs.comparePrice.style.display = 'none';
      }
    }

    // Toggle between ATC and BIS buttons
    this.toggleButtons(variant.available, variant.id);
  }

  /**
   * Toggle sticky button visibility with animation
   * @param {boolean} visible - Whether to show the button
   */
  toggleVisibility(visible) {
    if (this.isVisible === visible) return;

    this.isVisible = visible;
    this.dataset.visible = visible;

    // Trigger haptic feedback when button appears (if supported)
    if (visible && navigator.vibrate) {
      navigator.vibrate(10);
    }
  }

  /**
   * Handle opening the back-in-stock modal
   */
  handleOpenBisModal(event) {
    const button = event.target.closest('button');
    if (!button) return;

    const productId = button.getAttribute('data-product-id');
    const variantId = button.getAttribute('data-variant-id');

    // Find and open the BIS modal
    const modalId = `back-in-stock-modal-${productId}-${variantId}`;
    const modal = document.getElementById(modalId);

    if (modal && typeof modal.showDialog === 'function') {
      modal.showDialog();
    }
  }

  /**
   * Handle add-to-cart button click
   */
  async handleAddToCart() {
    // Prevent multiple clicks
    if (this.refs.stickyButton.disabled || this.refs.stickyButton.classList.contains('atc-added')) {
      return;
    }

    // Get form data from main product form
    const formData = new FormData(this.productForm);

    try {
      // Add loading state
      this.refs.stickyButton.classList.add('loading');

      // Submit to cart - pass formData through fetchConfig
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch('/cart/add.js', fetchCfg);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Show success state
      this.showSuccessState();

      // Trigger cart update event
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { items: [data] }
      }));

      // Open cart drawer if enabled
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && window.matchMedia('(max-width: 749px)').matches) {
        cartDrawer.open();
      }

      // Haptic feedback for success
      if (navigator.vibrate) {
        navigator.vibrate([50, 50, 50]);
      }

    } catch (error) {
      this.showErrorState();
    } finally {
      this.refs.stickyButton.classList.remove('loading');
    }
  }

  /**
   * Show success state animation
   */
  showSuccessState() {
    this.refs.stickyButton.classList.add('atc-added');

    setTimeout(() => {
      this.refs.stickyButton.classList.remove('atc-added');
    }, 2000);
  }

  /**
   * Show error state
   */
  showErrorState() {
    // Could add error UI here if needed
  }

  /**
   * Format money using Shopify's money format
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
    }

    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

customElements.define('sticky-add-to-cart-component', StickyAddToCartComponent);
